import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'es' | 'en';

const SITE_URL = 'https://ugclab.es';

function detectBrowserLanguage(): Language {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'es';
  try {
    const browserLang = navigator.language.toLowerCase().split('-')[0];
    return browserLang === 'en' ? 'en' : 'es';
  } catch {
    return 'es';
  }
}

function safeLocalStorageGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

interface I18nState {
  language: Language;
  setLanguage: (lang: Language) => void;
  initialized: boolean;
  initialize: () => void;
}

export const useI18n = create<I18nState>()(
  persist(
    (set, get) => ({
      language: 'es',
      initialized: false,
      setLanguage: (language) => set({ language }),
      initialize: () => {
        if (!get().initialized) {
          const stored = safeLocalStorageGet('ugclab-language');
          if (!stored) {
            const detected = detectBrowserLanguage();
            set({ language: detected, initialized: true });
          } else {
            set({ initialized: true });
          }
        }
      },
    }),
    {
      name: 'ugclab-language',
      partialize: (state) => ({ language: state.language }),
    }
  )
);

export interface HreflangLink {
  lang: string;
  url: string;
}

export function generateHreflangLinks(currentPath: string): HreflangLink[] {
  const cleanPath = currentPath.replace(/^\/(es|en)/, '');
  const basePath = cleanPath || '/';
  
  return [
    { lang: 'es', url: `${SITE_URL}${basePath}` },
    { lang: 'x-default', url: `${SITE_URL}${basePath}` },
  ];
}

export function getCanonicalUrl(currentPath: string): string {
  const cleanPath = currentPath.replace(/^\/(es|en)/, '');
  const basePath = cleanPath || '/';
  return `${SITE_URL}${basePath}`;
}

const translations: Record<Language, Record<string, string>> = {
  es: {
    'nav.home': 'Inicio',
    'nav.agency': 'Agencia',
    'nav.academy': 'Academia',
    'nav.creators': 'Creadores',
    'nav.events': 'Eventos',
    'nav.podcast': 'Podcast',
    'nav.blog': 'Blog',
    'nav.contact': 'Contacto',
    'nav.login': 'Iniciar sesión',
    
    'hero.title': 'Contenido que conecta',
    'hero.subtitle': 'Agencia y academia de creadores UGC',
    'hero.cta.brands': 'Soy Marca',
    'hero.cta.creators': 'Soy Creador',
    
    'agency.title': 'Agencia UGC',
    'agency.subtitle': 'Conectamos marcas con creadores auténticos',
    'agency.cta': 'Solicitar propuesta',
    
    'academy.title': 'Academia UGC',
    'academy.subtitle': 'Aprende a monetizar tu contenido',
    'academy.cta': 'Ver cursos',
    'academy.enroll': 'Inscribirse',
    'academy.enrolled': 'Inscrito',
    
    'creators.title': 'Nuestros Creadores',
    'creators.subtitle': 'Talento auténtico para tu marca',
    'creators.view': 'Ver perfil',
    
    'events.title': 'Eventos',
    'events.subtitle': 'Workshops, masterclasses y más',
    'events.register': 'Reservar plaza',
    'events.free': 'Gratis',
    
    'podcast.title': 'Podcast',
    'podcast.subtitle': 'Escucha las últimas tendencias UGC',
    'podcast.listen': 'Escuchar',
    
    'blog.title': 'Blog',
    'blog.subtitle': 'Artículos y recursos para creadores',
    'blog.read': 'Leer más',
    
    'contact.title': 'Contacto',
    'contact.subtitle': '¿Tienes un proyecto en mente?',
    'contact.send': 'Enviar mensaje',
    'contact.success': 'Mensaje enviado correctamente',
    
    'footer.rights': 'Todos los derechos reservados',
    'footer.privacy': 'Privacidad',
    'footer.terms': 'Términos',
    
    'common.loading': 'Cargando...',
    'common.error': 'Ha ocurrido un error',
    'common.retry': 'Reintentar',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.all': 'Todos',
    'common.none': 'Ninguno',
    'common.yes': 'Sí',
    'common.no': 'No',
    
    'dashboard.welcome': 'Bienvenido',
    'dashboard.overview': 'Resumen',
    'dashboard.projects': 'Proyectos',
    'dashboard.clients': 'Clientes',
    'dashboard.invoices': 'Facturas',
    'dashboard.creators': 'Creadores',
    'dashboard.courses': 'Cursos',
    'dashboard.settings': 'Configuración',
    'dashboard.logout': 'Cerrar sesión',
    
    'auth.login': 'Iniciar sesión',
    'auth.register': 'Registrarse',
    'auth.email': 'Correo electrónico',
    'auth.password': 'Contraseña',
    'auth.forgot': '¿Olvidaste tu contraseña?',
    'auth.noAccount': '¿No tienes cuenta?',
    'auth.hasAccount': '¿Ya tienes cuenta?',
  },
  en: {
    'nav.home': 'Home',
    'nav.agency': 'Agency',
    'nav.academy': 'Academy',
    'nav.creators': 'Creators',
    'nav.events': 'Events',
    'nav.podcast': 'Podcast',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.login': 'Login',
    
    'hero.title': 'Content that connects',
    'hero.subtitle': 'UGC creators agency and academy',
    'hero.cta.brands': 'I\'m a Brand',
    'hero.cta.creators': 'I\'m a Creator',
    
    'agency.title': 'UGC Agency',
    'agency.subtitle': 'We connect brands with authentic creators',
    'agency.cta': 'Request proposal',
    
    'academy.title': 'UGC Academy',
    'academy.subtitle': 'Learn to monetize your content',
    'academy.cta': 'View courses',
    'academy.enroll': 'Enroll',
    'academy.enrolled': 'Enrolled',
    
    'creators.title': 'Our Creators',
    'creators.subtitle': 'Authentic talent for your brand',
    'creators.view': 'View profile',
    
    'events.title': 'Events',
    'events.subtitle': 'Workshops, masterclasses and more',
    'events.register': 'Register',
    'events.free': 'Free',
    
    'podcast.title': 'Podcast',
    'podcast.subtitle': 'Listen to the latest UGC trends',
    'podcast.listen': 'Listen',
    
    'blog.title': 'Blog',
    'blog.subtitle': 'Articles and resources for creators',
    'blog.read': 'Read more',
    
    'contact.title': 'Contact',
    'contact.subtitle': 'Have a project in mind?',
    'contact.send': 'Send message',
    'contact.success': 'Message sent successfully',
    
    'footer.rights': 'All rights reserved',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.retry': 'Retry',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    
    'dashboard.welcome': 'Welcome',
    'dashboard.overview': 'Overview',
    'dashboard.projects': 'Projects',
    'dashboard.clients': 'Clients',
    'dashboard.invoices': 'Invoices',
    'dashboard.creators': 'Creators',
    'dashboard.courses': 'Courses',
    'dashboard.settings': 'Settings',
    'dashboard.logout': 'Logout',
    
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgot': 'Forgot password?',
    'auth.noAccount': 'Don\'t have an account?',
    'auth.hasAccount': 'Already have an account?',
  },
};

export function t(key: string, lang?: Language): string {
  const currentLang = lang || useI18n.getState().language;
  return translations[currentLang]?.[key] || translations['es'][key] || key;
}

export function useTranslation() {
  const { language, setLanguage } = useI18n();
  
  const translate = (key: string): string => {
    return translations[language]?.[key] || translations['es'][key] || key;
  };
  
  return {
    t: translate,
    language,
    setLanguage,
    languages: ['es', 'en'] as Language[],
  };
}
